import { useEffect, useState } from 'react';
import { EventBus, useGameStore } from '@campus/core';
import { Button, Icon, LogoMark } from '../ds/index.js';
import { sessionInfo } from '../people/membersStore.js';
import {
  NUDGE_SESSION_MIN_MS, noteVisit, isNudgeDue,
  markNudgeShown, markNudgeDismissed, markNudgeOptOut,
} from '../../lib/feedbackNudge.js';

const MESSAGES: Array<{ title: string; text: string }> = [
  {
    title: 'Placio se construit avec toi',
    text: 'Un bug, un truc pas pratique, une idée qui te ferait gagner du temps ? Dis-le-nous : chaque retour est lu par l’équipe.',
  },
  {
    title: 'Une idée pour le campus ?',
    text: 'Ce qui te manque, ce qui t’agace, ce que tu aimerais voir ici : deux minutes suffisent, et ça nous aide vraiment.',
  },
  {
    title: 'Quelque chose cloche ?',
    text: 'Un bouton qui ne répond pas, un son qui coupe, un avatar coincé ? Signale-le : une capture du campus est jointe d’office.',
  },
];

const TICK_MS = 30_000;

/** Moment calme : dans le campus, pas en discussion, aucune fenêtre ouverte. */
export function isQuietMoment(): boolean {
  const s = useGameStore.getState();
  return s.authState === 'in-game' && !s.adminPage && !s.isTransitioning && !s.localBubbleId
    && !!s.currentZoneId && s.realtimeStatus === 'connected'
    && document.visibilityState === 'visible'
    && !document.querySelector('.pl-modal');
}

/**
 * FeedbackNudge — petite carte qui invite de temps en temps les membres à
 * signaler un problème ou proposer une idée (fréquence : lib/feedbackNudge.ts).
 * Jamais pour les invités, jamais pendant une discussion ou une fenêtre ouverte.
 * Dev : `?feedbackNudge=1` l'affiche après 3 s, sans tenir compte des règles.
 */
export function FeedbackNudge() {
  const authState = useGameStore((s) => s.authState);
  const [message, setMessage] = useState<(typeof MESSAGES)[number] | null>(null);

  useEffect(() => {
    if (authState !== 'in-game') return;
    const { userId, isGuest } = sessionInfo();
    if (!userId || isGuest) return;
    noteVisit(userId);

    const show = () => {
      markNudgeShown(userId);
      setMessage(MESSAGES[Math.floor(Math.random() * MESSAGES.length)]!);
    };

    if (import.meta.env.DEV && new URLSearchParams(window.location.search).has('feedbackNudge')) {
      const t = setTimeout(show, 3000);
      return () => clearTimeout(t);
    }

    // Temps passé dans le campus, onglet visible (les minuteries ralentissent en arrière-plan).
    let active = 0;
    let last = Date.now();
    const iv = setInterval(() => {
      const now = Date.now();
      if (document.visibilityState === 'visible') active += Math.min(now - last, TICK_MS * 2);
      last = now;
      if (active >= NUDGE_SESSION_MIN_MS && isNudgeDue(userId, now) && isQuietMoment()) {
        clearInterval(iv);
        show();
      }
    }, TICK_MS);
    return () => clearInterval(iv);
  }, [authState]);

  // On quitte le campus (Admin, déconnexion…) : la carte disparaît sans compter comme « Plus tard ».
  useEffect(() => { if (authState !== 'in-game') setMessage(null); }, [authState]);

  if (!message) return null;
  const { userId } = sessionInfo();
  const later = () => { markNudgeDismissed(userId); setMessage(null); };
  const open = (kind: 'bug' | 'idea') => { setMessage(null); EventBus.emit('ui:feedback-open', { kind }); };
  const optOut = () => { markNudgeOptOut(userId); setMessage(null); };

  return (
    <div className="pl-pop pl-nudge" role="dialog" aria-labelledby="pl-nudge-title">
      <div className="pl-nudge-head">
        <LogoMark size={32} />
        <div className="pl-nudge-title" id="pl-nudge-title">{message.title}</div>
        <button type="button" className="pl-toast-close" onClick={later} aria-label="Plus tard">
          <Icon name="x" size={14} />
        </button>
      </div>
      <p className="pl-nudge-text">{message.text}</p>
      <div className="pl-nudge-actions">
        <Button variant="secondary" size="sm" onClick={() => open('bug')}>Signaler un problème</Button>
        <Button variant="primary" size="sm" onClick={() => open('idea')}>Proposer une idée</Button>
      </div>
      <div className="pl-nudge-foot">
        <button type="button" className="pl-nudge-link" onClick={later}>Plus tard</button>
        <button type="button" className="pl-nudge-link" onClick={optOut}>Ne plus me le proposer</button>
      </div>
    </div>
  );
}
